<app-drawer
  [isOpen]="isOpen"
  [title]="drawerTitle"
  [width]="constants.drawer.masterClauseWidth"
  [maxWidth]="constants.drawer.maxWidth"
  (closed)="onDrawerClose()"
>
  @if (form) {
    <div class="playbook-master-clause-drawer" [formGroup]="form">
      <div class="playbook-master-clause-drawer__content">
        <section class="playbook-master-clause-drawer__accordion">
          <button
            class="playbook-master-clause-drawer__accordion-header"
            type="button"
            (click)="toggleProperties()"
          >
            <span>{{ 'titles.clauseProperties' | transloco }}</span>
            <span class="material-icons-outlined">
              {{ isPropertiesExpanded ? 'expand_less' : 'expand_more' }}
            </span>
          </button>

          @if (isPropertiesExpanded) {
            <div class="playbook-master-clause-drawer__accordion-body">
              <div class="playbook-master-clause-drawer__field" data-focus-target="clauseName">
                <div class="playbook-master-clause-drawer__label">
                  {{ 'labels.clauseName' | transloco }}
                </div>
                <div class="playbook-master-clause-drawer__control">
                  <app-input
                    placeholder="{{ 'placeholders.clauseName' | transloco }}"
                    formControlName="clauseName"
                  ></app-input>
                </div>
              </div>

              <div class="playbook-master-clause-drawer__field" data-focus-target="languageId">
                <div class="playbook-master-clause-drawer__label">
                  {{ 'labels.clauseLanguage' | transloco }}
                </div>
                <div class="playbook-master-clause-drawer__control">
                  <app-single-select-autocomplete
                    class="w100"
                    formControlName="languageId"
                    [autocompleteOptions]="contentLanguages"
                    optionLabel="displayName"
                  ></app-single-select-autocomplete>
                </div>
              </div>

              <div
                class="playbook-master-clause-drawer__field"
                data-focus-target="mostDesiredOptionText"
              >
                <div class="playbook-master-clause-drawer__label">
                  {{ 'labels.mostDesiredOption' | transloco }}
                </div>
                <div class="playbook-master-clause-drawer__control">
                  <app-textarea
                    placeholder="{{ 'placeholders.mostDesiredOption' | transloco }}"
                    formControlName="mostDesiredOptionText"
                    [showCounter]="true"
                    [showErrors]="false"
                    [maxLength]="clauseInstructionsMaxSize"
                  ></app-textarea>
                </div>
              </div>

              <div
                class="playbook-master-clause-drawer__field"
                data-focus-target="fallbackOptionText"
              >
                <div class="playbook-master-clause-drawer__label">
                  {{ 'labels.fallbackOption' | transloco }}
                </div>
                <div class="playbook-master-clause-drawer__control">
                  <app-textarea
                    placeholder="{{ 'placeholders.fallbackOption' | transloco }}"
                    formControlName="fallbackOptionText"
                    [showCounter]="true"
                    [showErrors]="false"
                    [maxLength]="clauseInstructionsMaxSize"
                  ></app-textarea>
                </div>
              </div>

              <div
                class="playbook-master-clause-drawer__field"
                data-focus-target="unacceptableOptionText"
              >
                <div class="playbook-master-clause-drawer__label">
                  {{ 'labels.unacceptableOption' | transloco }}
                </div>
                <div class="playbook-master-clause-drawer__control">
                  <app-textarea
                    placeholder="{{ 'placeholders.unacceptableOption' | transloco }}"
                    formControlName="unacceptableOptionText"
                    [showCounter]="true"
                    [showErrors]="false"
                    [maxLength]="clauseInstructionsMaxSize"
                  ></app-textarea>
                </div>
              </div>

              @if (atLeastOneOptionValid === false) {
                <div class="playbook-master-clause-drawer__error error-text">
                  {{ 'errors.addClauseForm' | transloco }}
                </div>
              }

              <div class="playbook-master-clause-drawer__warning">
                <span class="material-icons-outlined">warning_amber</span>
                <span>{{ 'text.masterClauseWarning' | transloco }}</span>
              </div>

              @if (canWriteClauses) {
              <div class="playbook-master-clause-drawer__actions">
                <app-button
                  [type]="btnTypes.Flat"
                  [size]="btnSizes.Basic"
                  [label]="'buttons.update' | transloco"
                  [disabled]="!!form.invalid || isBusy"
                  (onClick)="onConfirm()"
                ></app-button>
                <app-button
                  [type]="btnTypes.Stroked"
                  [size]="btnSizes.Basic"
                  [label]="'buttons.cancel' | transloco"
                  [disabled]="isSaving"
                  (onClick)="onCancel()"
                ></app-button>
              </div>
              }
            </div>
          }
        </section>

        <section
          class="playbook-master-clause-drawer__accordion playbook-master-clause-drawer__accordion--spaced"
        >
          <button
            class="playbook-master-clause-drawer__accordion-header"
            type="button"
            (click)="toggleLinkedPlaybooks()"
          >
            <span>{{ 'titles.playbooksUsingThisClause' | transloco }}</span>
            <span class="material-icons-outlined">
              {{ areLinkedPlaybooksExpanded ? 'expand_less' : 'expand_more' }}
            </span>
          </button>

          @if (areLinkedPlaybooksExpanded) {
            <div class="playbook-master-clause-drawer__accordion-body">
              @if (linkedPlaybooks.length) {
                <div class="playbook-master-clause-drawer__linked-playbooks">
                  @for (playbook of linkedPlaybooks; track playbook.uniqueId) {
                    <div class="playbook-master-clause-drawer__linked-playbook">
                      <button
                        class="playbook-master-clause-drawer__linked-playbook-name link-like"
                        type="button"
                        (click)="openPlaybookDetails(playbook.uniqueId)"
                      >
                        {{ buildPreviewName(playbook) }}
                      </button>
                      @if (canWriteClauses) {
                        <app-button
                          icon="link_off"
                          [type]="btnTypes.Stroked"
                          [size]="btnSizes.Basic"
                          [label]="'actions.unlink' | transloco"
                          [disabled]="isSaving || isUnlinkingPlaybook(playbook.uniqueId)"
                          (onClick)="unlinkFromPlaybook(playbook)"
                        ></app-button>
                      }
                    </div>
                  }
                </div>
              } @else {
                <div class="playbook-master-clause-drawer__empty-state">
                  {{ 'text.noPlaybooksUsingThisClause' | transloco }}
                </div>
              }

              @if (canWriteClauses && linkPlaybookForm) {
                <div
                  class="playbook-master-clause-drawer__link-row"
                  [formGroup]="linkPlaybookForm"
                  data-focus-target="playbooks"
                >
                  <div class="playbook-master-clause-drawer__link-select">
                    <app-single-select-autocomplete
                      formControlName="playbookId"
                      [autocompleteOptions]="availablePlaybooks"
                      optionLabel="displayName"
                      optionValue="uniqueId"
                      [placeholderKey]="'titles.playbook'"
                    ></app-single-select-autocomplete>
                  </div>
                  <app-button
                    icon="link"
                    [type]="btnTypes.Flat"
                    [size]="btnSizes.Basic"
                    [label]="'buttons.link' | transloco"
                    [disabled]="linkPlaybookForm.invalid || isBusy || !canReadClauses"
                    (onClick)="linkToPlaybook()"
                  ></app-button>
                </div>
              }

              @if (canWriteClauses) {
              <div class="playbook-master-clause-drawer__create-playbook">
                <app-button
                  icon="add"
                  [type]="btnTypes.Flat"
                  [size]="btnSizes.Basic"
                  [label]="'buttons.createNewPlaybook' | transloco"
                  [disabled]="isBusy"
                  (onClick)="openCreatePlaybookForm()"
                ></app-button>

                @if (creatingNewPlaybook && createPlaybookForm) {
                  <div class="arrow-up arrow-up--inline"></div>
                  <div class="new-item-form" [formGroup]="createPlaybookForm">
                    <div class="row">
                      <div class="col">
                        {{ 'titles.playbookDetails' | transloco }}
                      </div>
                    </div>

                    <div class="row mt-2">
                      <div class="col-sm-12 col-lg-6">
                        <app-input
                          label="{{ 'labels.playbookName' | transloco }}"
                          placeholder="{{ 'placeholders.playbookNameSuffix' | transloco }}"
                          formControlName="name"
                        ></app-input>
                      </div>

                      <div class="col-sm-12 col-lg-6 mt-2 mt-lg-0">
                        <div
                          class="control-label playbook-master-clause-drawer__new-playbook-label"
                        >
                          {{ 'labels.docType' | transloco }}
                        </div>
                        <app-single-select-autocomplete
                          class="w100"
                          formControlName="documentTypeId"
                          [autocompleteOptions]="resolvedDocTypeItems"
                        ></app-single-select-autocomplete>
                      </div>
                    </div>

                    <div class="row mt-2">
                      <div class="col-sm-12 col-lg-6">
                        <div
                          class="control-label playbook-master-clause-drawer__new-playbook-label"
                        >
                          {{ 'labels.position' | transloco }}
                        </div>
                        <app-single-select-autocomplete
                          class="w100"
                          formControlName="positionId"
                          [autocompleteOptions]="positionItems"
                        ></app-single-select-autocomplete>
                      </div>

                      <div class="col-sm-12 col-lg-6 mt-2 mt-lg-0">
                        <div
                          class="control-label playbook-master-clause-drawer__new-playbook-label"
                        >
                          {{ 'columns.language' | transloco }}
                        </div>
                        <app-single-select-autocomplete
                          class="w100"
                          formControlName="languageId"
                          [autocompleteOptions]="contentLanguages"
                          optionLabel="displayName"
                        ></app-single-select-autocomplete>
                      </div>
                    </div>

                    <div class="row mt-2">
                      <div class="col">
                        <div class="playbook-master-clause-drawer__new-playbook-preview">
                          <mat-icon
                            class="playbook-master-clause-drawer__new-playbook-preview-icon"
                          >
                            info
                          </mat-icon>
                          <span>
                            {{ 'text.playbookSavedAs' | transloco: { name: playbookPreviewName } }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="new-item-form__footer">
                      <app-button
                        [type]="btnTypes.Flat"
                        [size]="btnSizes.Basic"
                        [label]="'buttons.saveChanges' | transloco"
                        [disabled]="createPlaybookForm.invalid || isBusy"
                        (onClick)="onConfirmCreatePlaybook()"
                      ></app-button>
                      <app-button
                        [type]="btnTypes.Stroked"
                        [size]="btnSizes.Basic"
                        [label]="'buttons.cancel' | transloco"
                        [disabled]="isSaving"
                        (onClick)="onCancelCreatePlaybookForm()"
                      ></app-button>
                    </div>
                  </div>
                }
              </div>
              }
            </div>
          }
        </section>
      </div>
    </div>
  } @else {
    <div class="playbook-master-clause-drawer__loading">
      {{ 'text.loading' | transloco }}
    </div>
  }
</app-drawer>
